import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { useSite } from "@/lib/SiteContext";

export const NewsletterForm = ({ compact = false }) => {
  const { site } = useSite();
  const nl = site?.content?.newsletter || {};
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) {
      setError("Please tick the box to confirm you'd like to hear from us.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/newsletter", { email, consent });
      setDone(true);
      toast.success("You're on the list — thank you!");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <p className="text-sm font-semibold text-gold" data-testid="newsletter-success-message">
        You're on the list — recipes and exclusive offers are on their way.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className={compact ? "space-y-3" : "space-y-4"} noValidate>
      <div className="flex flex-col gap-3 sm:flex-row">
        <label htmlFor={compact ? "footer-newsletter-email" : "newsletter-email"} className="sr-only">
          Email address
        </label>
        <input
          id={compact ? "footer-newsletter-email" : "newsletter-email"}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.co.uk"
          className="w-full flex-1 rounded-xl border border-choco-line bg-choco px-4 py-2.5 text-sm text-cream-text placeholder:text-cream-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
          data-testid="newsletter-email-input"
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded-full bg-gold px-6 py-2.5 text-sm font-bold text-choco transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco active:scale-[0.98] disabled:opacity-60"
          data-testid="newsletter-subscribe-button"
        >
          {busy ? "Joining…" : (nl.button_label || "Subscribe")}
        </button>
      </div>
      <div className="flex items-start gap-2.5">
        <Checkbox
          id={compact ? "footer-newsletter-consent" : "newsletter-consent"}
          checked={consent}
          onCheckedChange={(v) => setConsent(v === true)}
          className="mt-0.5 border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco"
          data-testid="newsletter-consent-checkbox"
        />
        <label htmlFor={compact ? "footer-newsletter-consent" : "newsletter-consent"} className="text-xs leading-relaxed text-cream-muted">
          {nl.description || "I'd like to receive recipes and exclusive offers by email. You can unsubscribe at any time."}
        </label>
      </div>
      {error && (
        <p className="text-sm font-semibold text-red-400" role="alert" data-testid="newsletter-error-message">
          {error}
        </p>
      )}
    </form>
  );
};
